import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Undo2,
  Trash2,
  Save,
  Loader2,
  Sparkles,
  Lock,
  RefreshCw,
} from "lucide-react";
import { saveArt, listMyArt, deleteArt, saveArtDraft, loadArtDraft, deleteArtDraft } from "@/lib/art.functions";
import { ArtUpload } from "@/components/ArtUpload";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { CREATIVE_ACTIVITIES, findActivity } from "@/data/creative-activities";
import { DrawingControls } from "@/components/drawing/DrawingControls";


export const Route = createFileRoute("/art-board")({
  validateSearch: (search: Record<string, unknown>) =>
    z
      .object({ a: z.string().optional() })
      .parse(search),
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Art Board — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A gentle drawing space for self-reflection. Draw how you feel, your safe place, your inner child, hope, or healing — privately on your device.",
      },
      { property: "og:title", content: "Art Board — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "A gentle drawing space for self-reflection. Draw, save privately, and return whenever you need.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/art-board" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/art-board" }],
  }),
  component: withMemberGate(ArtBoardPage, "Art Board"),
});

const PROMPTS = [
  "Draw how you feel today",
  "Draw your safe place",
  "Draw comfort",
  "Draw hope",
  "Draw a memory",
  "Draw what safety feels like",
  "Draw your inner child",
  "Draw a colour that matches your mood",
];



type Stroke = {
  color: string;
  size: number;
  erase: boolean;
  points: { x: number; y: number }[];
};

const DRAFT_KEY_PREFIX = "art-board:draft:v2:";
const LEGACY_DRAFT_KEY = "art-board:draft:v1";

function draftKeyFor(slug: string | undefined): string {
  return `${DRAFT_KEY_PREFIX}${slug ?? "blank"}`;
}

type Draft = { strokes: Stroke[]; prompt?: string; title?: string; surface?: { width: number; height: number }; savedAt?: number };

function loadDraft(key: string): Draft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.strokes)) return null;
    return parsed as Draft;
  } catch {
    return null;
  }
}

function saveDraftLocal(key: string, draft: Draft) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify({ ...draft, savedAt: Date.now() }));
  } catch {
    // ignore quota errors
  }
}

function ArtBoardPage() {
  const search = Route.useSearch();
  const activity = useMemo(() => findActivity(search.a), [search.a]);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Drawing coordinates stay fixed while CSS scales the complete artwork.
  const surfaceRef = useRef<{ width: number; height: number; dpr: number } | null>(null);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const drawingRef = useRef<Stroke | null>(null);
  const [color, setColor] = useState("#141414");
  const [size, setSize] = useState(4);
  const [erase, setErase] = useState(false);
  const isAuthedRef = useRef(false);
  const [prompt, setPrompt] = useState<string>(
    activity ? activity.prompt : PROMPTS[0],
  );
  const [title, setTitle] = useState(activity ? activity.title : "");
  const [reflection, setReflection] = useState("");
  const [saveStep, setSaveStep] = useState<null | "reflect">(null);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [autosaveAt, setAutosaveAt] = useState<number | null>(null);
  const [restoredDraft, setRestoredDraft] = useState(false);

  const draftKey = useMemo(() => draftKeyFor(activity?.slug), [activity?.slug]);

  // Reset canvas + load per-activity draft whenever the activity changes.
  const dbDraftLoadedRef = useRef<string | null>(null);

  useEffect(() => {
    // Start every activity switch with a fresh canvas.
    drawingRef.current = null;
    setStrokes([]);
    setRestoredDraft(false);
    setAutosaveAt(null);
    setReflection("");
    setSaveStep(null);

    // Clean up any legacy shared-draft key from the old single-draft design.
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem(LEGACY_DRAFT_KEY);
      } catch {
        /* noop */
      }
    }

    // Reset prompt/title defaults for this activity.
    setPrompt(activity ? activity.prompt : PROMPTS[0]);
    setTitle(activity ? activity.title : "");

    // Restore this activity's own draft only.
    const draft = loadDraft(draftKey);
    if (draft && draft.strokes.length > 0) {
      if (draft.surface && Number.isFinite(draft.surface.width) && Number.isFinite(draft.surface.height) && draft.surface.width > 0 && draft.surface.height > 0) {
        surfaceRef.current = { ...draft.surface, dpr: Math.min(window.devicePixelRatio || 1, 2) };
      }
      setStrokes(draft.strokes);
      if (draft.prompt) setPrompt(draft.prompt);
      if (draft.title) setTitle(draft.title);
      setRestoredDraft(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  // Autosave (debounced) whenever strokes/prompt/title change — locally and,
  // for signed-in members, to their account so drafts survive restarts,
  // reinstalls and other devices.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const t = setTimeout(() => {
      if (strokes.length === 0) {
        try {
          localStorage.removeItem(draftKey);
        } catch {
          /* noop */
        }
        setAutosaveAt(null);
        if (isAuthedRef.current) {
          deleteDraftFn({ data: { draftKey } }).catch(() => {});
        }
        return;
      }
      const surface = surfaceRef.current
        ? { width: surfaceRef.current.width, height: surfaceRef.current.height }
        : undefined;
      saveDraftLocal(draftKey, { strokes, prompt, title, surface });
      setAutosaveAt(Date.now());
      if (isAuthedRef.current) {
        saveDraftFn({
          data: { draftKey, strokes, prompt, title, surface: surface ?? null },
        }).catch(() => {
          // offline or transient — local copy still safe
        });
      }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strokes, prompt, title, draftKey]);

  function discardDraft() {
    try {
      localStorage.removeItem(draftKey);
    } catch {
      /* noop */
    }
    if (isAuthedRef.current) {
      deleteDraftFn({ data: { draftKey } }).catch(() => {});
    }
    setAutosaveAt(null);
  }

  function saveDraftNow() {
    if (typeof window === "undefined") return;
    if (strokes.length === 0 && !drawingRef.current) {
      const msg = "Add a few marks to the canvas first.";
      setSaveStatus(msg);
      toast(msg);
      setTimeout(() => setSaveStatus(null), 2000);
      return;
    }
    try {
      const all = drawingRef.current
        ? [...strokes, drawingRef.current]
        : strokes;
      localStorage.setItem(
        draftKey,
        JSON.stringify({ strokes: all, prompt, title, surface: surfaceRef.current }),
      );
      const now = Date.now();
      setAutosaveAt(now);
      const msg = "Progress saved to this device.";
      setSaveStatus(msg);
      toast.success(msg, {
        description: `Saved at ${new Date(now).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
      });
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      const msg = "Couldn't save progress on this device.";
      setSaveStatus(msg);
      toast.error(msg, {
        description: err instanceof Error ? err.message : "Storage may be full or unavailable.",
      });
      setTimeout(() => setSaveStatus(null), 2500);
    }
  }

  const qc = useQueryClient();
  const fetchMine = useServerFn(listMyArt);
  const saveFn = useServerFn(saveArt);
  const delFn = useServerFn(deleteArt);
  const saveDraftFn = useServerFn(saveArtDraft);
  const loadDraftFn = useServerFn(loadArtDraft);
  const deleteDraftFn = useServerFn(deleteArtDraft);

  const [authReady, setAuthReady] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setIsAuthed(!!data.session);
      setAuthReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setIsAuthed(!!session);
      setAuthReady(true);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    isAuthedRef.current = isAuthed;
  }, [isAuthed]);

  // For signed-in members, load the account-saved draft. If it is newer than
  // the on-device copy (e.g. after a reinstall or on another device), use it.
  useEffect(() => {
    if (!authReady || !isAuthed) return;
    if (dbDraftLoadedRef.current === draftKey) return;
    dbDraftLoadedRef.current = draftKey;
    let cancelled = false;
    loadDraftFn({ data: { draftKey } })
      .then(({ draft }) => {
        if (cancelled || !draft || !Array.isArray(draft.strokes) || draft.strokes.length === 0) return;
        const local = loadDraft(draftKey);
        const dbAt = draft.updated_at ? new Date(draft.updated_at).getTime() : 0;
        const localAt = local?.savedAt ?? 0;
        if (local && localAt >= dbAt) return; // device copy is fresher
        const s = draft.surface as { width?: number; height?: number } | null;
        if (s && Number.isFinite(s.width) && Number.isFinite(s.height) && (s.width ?? 0) > 0 && (s.height ?? 0) > 0) {
          surfaceRef.current = { width: s.width!, height: s.height!, dpr: Math.min(window.devicePixelRatio || 1, 2) };
        }
        setStrokes(draft.strokes as Stroke[]);
        if (draft.prompt) setPrompt(draft.prompt);
        if (draft.title) setTitle(draft.title);
        setRestoredDraft(true);
        setAutosaveAt(dbAt || Date.now());
        saveDraftLocal(draftKey, {
          strokes: draft.strokes as Stroke[],
          prompt: draft.prompt ?? undefined,
          title: draft.title ?? undefined,
          surface: s && s.width && s.height ? { width: s.width, height: s.height } : undefined,
        });
      })
      .catch(() => {
        // offline — device copy still works
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, isAuthed, draftKey]);

  const { data, isLoading } = useQuery({
    queryKey: ["my-art"],
    queryFn: () => fetchMine(),
    enabled: isAuthed,
    retry: false,
  });

  const saveMut = useMutation({
    mutationFn: (payload: {
      imageDataUrl: string;
      title: string | null;
      prompt: string | null;
      description: string | null;
      visibility: "private" | "community" | "anonymous";
    }) => saveFn({ data: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-art"] });
      setSaveStatus("Saved privately to your gallery.");
      setSaveStep(null);
      setReflection("");
      setTitle(activity ? activity.title : "");
      clearCanvas();
      setTimeout(() => setSaveStatus(null), 3500);
    },
    onError: (e: Error) => setSaveStatus(`Couldn't save: ${e.message}`),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-art"] }),
  });

  // Setup canvas (responsive, DPR-aware)
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#fbf7ef"; // cream background
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    const all = [...strokes, ...(drawingRef.current ? [drawingRef.current] : [])];
    for (const s of all) {
      ctx.beginPath();
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = s.size;
      ctx.strokeStyle = s.erase ? "#fbf7ef" : s.color;
      const pts = s.points;
      if (pts.length === 1) {
        ctx.arc(pts[0].x, pts[0].y, s.size / 2, 0, Math.PI * 2);
        ctx.fillStyle = ctx.strokeStyle as string;
        ctx.fill();
      } else {
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
        ctx.stroke();
      }
    }
  }, [strokes]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapperRef.current;
    if (!canvas || !wrap) return;
    const width = Math.max(1, wrap.clientWidth);
    const surface = surfaceRef.current ?? { width, height: Math.round(width * 0.75), dpr: Math.min(window.devicePixelRatio || 1, 2) };
    surfaceRef.current = surface;
    const pixelWidth = Math.round(surface.width * surface.dpr);
    const pixelHeight = Math.round(surface.height * surface.dpr);
    // Never clear the backing store merely because the viewport changed.
    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }
    canvas.getContext("2d")?.setTransform(surface.dpr, 0, 0, surface.dpr, 0, 0);
    redraw();
  }, [redraw]);

  useEffect(() => {
    redraw();
  }, [strokes, redraw]);

  function getPos(e: React.PointerEvent) {
    const canvas = canvasRef.current;
    const surface = surfaceRef.current;
    if (!canvas || !surface) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: (e.clientX - rect.left) * surface.width / rect.width, y: (e.clientY - rect.top) * surface.height / rect.height };
  }

  function onDown(e: React.PointerEvent) {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drawingRef.current = {
      color,
      size,
      erase,
      points: [getPos(e)],
    };
    redraw();
  }
  function onMove(e: React.PointerEvent) {
    if (!drawingRef.current) return;
    drawingRef.current.points.push(getPos(e));
    redraw();
  }
  function onUp() {
    const finished = drawingRef.current;
    if (finished) {
      drawingRef.current = null;
      setStrokes((s) => [...s, finished]);
    }
  }

  function undo() {
    setStrokes((s) => s.slice(0, -1));
  }
  function clearCanvas() {
    setStrokes([]);
    drawingRef.current = null;
    discardDraft();
    setRestoredDraft(false);
    redraw();
  }

  /** Called when the user taps the primary Save button on the toolbar. */
  function handleSaveClick() {
    if (!isAuthed) {
      setSaveStatus("Please sign in to save your artwork.");
      setTimeout(() => setSaveStatus(null), 3500);
      return;
    }
    if (strokes.length === 0) {
      setSaveStatus("Add a few marks to the canvas first.");
      setTimeout(() => setSaveStatus(null), 2500);
      return;
    }
    // Step 2: offer optional reflection before persisting.
    setSaveStep("reflect");
  }

  /** Persist the drawing (with or without a reflection). */
  function persistDrawing(withReflection: boolean) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    saveMut.mutate({
      imageDataUrl: url,
      title: title.trim() || (activity ? activity.title : null),
      prompt: prompt || null,
      description: withReflection ? reflection.trim() || null : null,
      visibility: "private",
    });
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 pb-8 pt-2">
      {activity ? (
        <header className="mb-5 rounded-3xl border border-gold/30 bg-gradient-to-br from-cream/70 via-card to-card p-5 shadow-sm sm:p-7">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-background/70 text-3xl shadow-inner">
              <span aria-hidden>{activity.emoji}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--gold-deep)]">
                Creative reflection
              </p>
              <h1 className="mt-1 font-display text-2xl text-foreground sm:text-3xl">
                {activity.title}
              </h1>
              <p className="mt-2 text-sm text-foreground/80 sm:text-base">
                {activity.prompt}
              </p>
              {activity.bullets && (
                <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                  {activity.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Sparkles className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--gold-deep)]" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-foreground/60">
                <Lock className="h-3 w-3" /> Private to you when you save.
              </p>
            </div>
          </div>
        </header>
      ) : (
        <>
          <header className="mb-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-violet-100 px-3 py-1 text-xs font-medium text-violet-800">
              <Sparkles className="h-3.5 w-3.5" /> Art Board
            </div>
            <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
              A gentle space to draw
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-foreground/70">
              Make marks, scribble, or sketch — there is no right way. Saved
              artwork is{" "}
              <span className="inline-flex items-center gap-1 font-medium">
                <Lock className="h-3.5 w-3.5" /> private to you
              </span>
              .
            </p>
          </header>

          {/* Single rotating prompt (blank-canvas mode only) */}
          <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-violet-200/70 bg-violet-50/60 px-4 py-3">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-violet-800/70">
                Optional prompt
              </div>
              <p className="truncate font-display text-sm text-foreground sm:text-base">
                {prompt}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                const others = PROMPTS.filter((p) => p !== prompt);
                const next = others[Math.floor(Math.random() * others.length)];
                setPrompt(next);
              }}
              aria-label="Show another prompt"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-violet-300 bg-white px-3 py-1.5 text-xs font-medium text-violet-800 transition hover:bg-violet-100 dark:border-violet-400/40 dark:bg-card dark:text-violet-200 dark:hover:bg-violet-500/15"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              New prompt
            </button>
          </div>
        </>
      )}

      {/* Quick switcher to other activities */}
      {activity && (
        <div className="mb-4 -mx-1 flex snap-x snap-mandatory gap-2 overflow-x-auto px-1 pb-1">
          {CREATIVE_ACTIVITIES.filter((a) => a.slug !== activity.slug).map((a) => (
            <Link
              key={a.slug}
              to="/art-board"
              search={{ a: a.slug }}
              className="inline-flex shrink-0 snap-start items-center gap-1.5 rounded-full border border-gold/30 bg-card/70 px-3 py-1.5 text-xs font-medium text-foreground/75 transition hover:border-gold/60 hover:text-foreground"
            >
              <span aria-hidden>{a.emoji}</span> {a.title}
            </Link>
          ))}
        </div>
      )}


      {/* Canvas */}
      <div
        ref={wrapperRef}
        className="overflow-hidden rounded-2xl border border-border bg-[#fbf7ef] shadow-sm"
      >
        <canvas
          ref={canvasRef}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onLostPointerCapture={onUp}
          onPointerLeave={onUp}
          className="block h-auto w-full touch-none"
          aria-label="Drawing area"
          style={{ cursor: "crosshair" }}
        />
      </div>

      {/* Autosave / restored indicator */}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-foreground/60">
        <div>
          {restoredDraft && strokes.length > 0 ? (
            <span className="inline-flex items-center gap-2">
              <span>Restored your previous drawing.</span>
              <button
                type="button"
                onClick={clearCanvas}
                className="rounded-md border border-border px-2 py-1 text-[11px] transition hover:bg-accent"
              >
                Start fresh
              </button>
            </span>
          ) : null}
        </div>
        <div aria-live="polite" className="inline-flex items-center gap-2">
          <span>
            {autosaveAt
              ? `Autosaved ${new Date(autosaveAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
              : strokes.length > 0
                ? "Saving…"
                : "Autosaves to this device"}
          </span>
          {strokes.length > 0 && (
            <button
              type="button"
              onClick={saveDraftNow}
              className="underline underline-offset-2 transition hover:text-foreground"
            >
              Save now
            </button>
          )}
        </div>
      </div>

      {/* Drawing tools */}
      <DrawingControls color={color} onColor={setColor} size={size} onSize={setSize} erase={erase} onErase={setErase} />

      {/* Actions */}
      <div className="mt-3 rounded-2xl border border-border bg-card p-3 shadow-sm dark:bg-card/70">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={undo}
            disabled={strokes.length === 0}
            className="inline-flex min-h-[44px] touch-manipulation items-center gap-1.5 rounded-xl border border-border px-3.5 text-sm font-medium transition hover:bg-accent disabled:pointer-events-none disabled:opacity-40"
          >
            <Undo2 className="h-4 w-4" /> Undo
          </button>
          <button
            type="button"
            onClick={() => {
              if (strokes.length === 0) return;
              if (confirm("Clear the canvas? This can't be undone.")) clearCanvas();
            }}
            disabled={strokes.length === 0}
            className="inline-flex min-h-[44px] touch-manipulation items-center gap-1.5 rounded-xl border border-border px-3.5 text-sm font-medium text-foreground/70 transition hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" /> Clear
          </button>
          <button
            type="button"
            onClick={handleSaveClick}
            disabled={strokes.length === 0 || saveMut.isPending}
            className="ms-auto inline-flex min-h-[44px] w-full touch-manipulation items-center justify-center gap-2 rounded-xl bg-[var(--gradient-gold)] px-5 text-sm font-semibold text-foreground shadow-[var(--shadow-gold)] ring-1 ring-gold/50 transition hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-50 sm:w-auto"
          >
            {saveMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saveMut.isPending ? "Saving…" : "Save drawing"}
          </button>
        </div>
      </div>

      {saveStatus && (
        <div
          role="status"
          className="mt-3 rounded-xl border border-gold/40 bg-gold/10 px-3 py-2 text-sm text-foreground/80"
        >
          {saveStatus}
        </div>
      )}


      {/* Upload artwork */}
      {isAuthed && <ArtUpload />}

      {/* My Artwork */}
      <section className="mt-10">
        <h2 className="text-lg font-semibold">My Artwork</h2>
        <p className="text-sm text-foreground/60">
          Only you can see these. They are stored privately.
        </p>

        <div className="mt-4">
          {!authReady ? (
            <div className="flex items-center gap-2 text-sm text-foreground/60">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !isAuthed ? (
            <div className="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-foreground/60">
              <p>Sign in to save your drawings and view your private gallery.</p>
              <Link
                to="/login"
                className="mt-3 inline-flex items-center justify-center rounded-lg bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:opacity-90"
              >
                Sign in
              </Link>
            </div>
          ) : isLoading ? (
            <div className="flex items-center gap-2 text-sm text-foreground/60">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading your gallery…
            </div>
          ) : !data?.pieces.length ? (
            <div className="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-foreground/60">
              No artwork yet. Your saved drawings will appear here.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {data.pieces.map((p) => (
                <figure
                  key={p.id}
                  className="group relative overflow-hidden rounded-xl border border-border bg-[#fbf7ef] shadow-sm"
                >
                  {p.url ? (
                    <img
                      src={p.url}
                      alt={p.title || "Artwork"}
                      className="aspect-[4/3] w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex aspect-[4/3] w-full items-center justify-center text-xs text-foreground/40">
                      Preview unavailable
                    </div>
                  )}
                  <figcaption className="space-y-0.5 p-2 text-xs">
                    <div className="truncate font-medium text-foreground/80">
                      {p.title || "Untitled"}
                    </div>
                    {p.prompt && (
                      <div className="truncate text-foreground/50">
                        {p.prompt}
                      </div>
                    )}
                  </figcaption>
                  <button
                    onClick={() => {
                      if (confirm("Delete this artwork? This can't be undone."))
                        delMut.mutate(p.id);
                    }}
                    className="absolute end-1.5 top-1.5 rounded-full bg-background/90 p-1.5 opacity-0 shadow transition group-hover:opacity-100"
                    aria-label="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>

      <p className="mt-10 text-center text-xs text-foreground/50">
        <Link to="/creative-reflection" className="underline">
          Back to Creative Reflection
        </Link>
      </p>

      {/* Optional reflection step, shown after Save is tapped */}
      {saveStep === "reflect" && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
          onClick={() => !saveMut.isPending && setSaveStep(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl bg-background p-6 shadow-xl"
          >
            <h3 className="font-display text-xl text-foreground">
              Would you like to add a reflection?
            </h3>
            <p className="mt-1 text-sm text-foreground/60">
              Optional — a few words about what came up as you drew. You can
              always skip this and just save your drawing.
            </p>

            <label className="mt-4 block text-xs font-medium text-foreground/70">
              Reflection (optional)
            </label>
            <textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              maxLength={2000}
              rows={4}
              placeholder="What did you notice? How does the drawing feel to look at?"
              className="mt-1 w-full resize-none rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-foreground/20"
            />

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
              <button
                disabled={saveMut.isPending}
                onClick={() => persistDrawing(false)}
                className="rounded-full border border-border px-4 py-2 text-sm hover:bg-accent disabled:opacity-60"
              >
                Skip &amp; save drawing
              </button>
              <button
                disabled={saveMut.isPending}
                onClick={() => persistDrawing(true)}
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background hover:opacity-90 disabled:opacity-60"
              >
                {saveMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Lock className="h-3.5 w-3.5" />
                )}
                Add reflection &amp; save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
